import Vue from 'vue';
// axiosInstance, not the cachios client — the page is the landing spot
// after saving career details, and a cached copy would show stale numbers
import {axiosInstance as http} from "@/http_client";

/**
 * Professional Development: the expanded clock-hour rollup behind the
 * section's pages (GET app/pd — award ledger, by-year histogram, in-progress
 * rows, pace) plus a per-district cache of published salary schedules
 * (GET districts/:id/pay-scale) shared by the dashboard hero, the overview
 * and the Salary Schedule page.
 *
 * The dashboard feed carries the light `pd` block; this module never
 * replaces it, so the two stay in step (same server builder).
 */
export default {
    state: {
        summary: null,
        loading: false,
        error: null,
        // district id → scale object | null (no published schedule)
        scales: {},
        scalePromises: {},
    },
    getters: {
        pdSummary: state => state.summary,
        pdLoading: state => state.loading,
        pdError: state => state.error,
        // undefined = not loaded yet, null = no published schedule
        payScaleFor: state => districtId => state.scales[districtId],
    },
    mutations: {
        setPdSummary(state, summary) {
            state.summary = summary;
            state.error = null;
        },
        setPdLoading(state, loading) {
            state.loading = loading;
        },
        setPdError(state, err) {
            state.error = err;
        },
        setPayScale(state, {districtId, scale}) {
            Vue.set(state.scales, districtId, scale);
        },
        clearPd(state) {
            state.summary = null;
            state.error = null;
        },
    },
    actions: {
        getPd({commit, rootGetters}) {
            // Drop a response that arrives after the user changed (portal
            // login-as-user relay) — see dashboard.js
            const forUser = rootGetters.currentUser && rootGetters.currentUser.id;
            commit('setPdLoading', true);
            return http.get('app/pd').then(response => {
                const nowUser = rootGetters.currentUser && rootGetters.currentUser.id;
                if (nowUser !== forUser) return null;
                commit('setPdSummary', response.data);
                return response.data;
            }).catch(err => {
                console.error('Error loading professional development:', err);
                commit('setPdError', err);
                throw err;
            }).finally(() => {
                commit('setPdLoading', false);
            });
        },
        // Resolves to the published schedule or null; one request per
        // district per session
        getPayScale({state, commit}, districtId) {
            if (!districtId) return Promise.resolve(null);
            if (state.scales[districtId] !== undefined) return Promise.resolve(state.scales[districtId]);
            if (state.scalePromises[districtId]) return state.scalePromises[districtId];
            const promise = http.get('districts/' + districtId + '/pay-scale').then(response => {
                commit('setPayScale', {districtId, scale: response.data});
                return response.data;
            }).catch(() => {
                // 404 = no published schedule for this district
                commit('setPayScale', {districtId, scale: null});
                return null;
            }).finally(() => {
                delete state.scalePromises[districtId];
            });
            state.scalePromises[districtId] = promise;
            return promise;
        },
    }
};
